// ============================================================
// THE DYING CLOCK, under a second mortal wound.
//
// Separate from `protagonist.ts` because it reads `conditions.ts`, and
// `protagonist.ts` must stay dependency-free - `types.ts` imports it.
// Same reason `critCap.ts` lives apart. See that file's header.
//
// ANOTHER MORTAL WOUND CLOSES THE WINDOW; it does not open it. That
// sounds obvious and the app did the opposite: `mergeCondition` under
// the Round Countdown ruling ADDS durations, so a second `Dy3` on a man
// at `Dy3` left him at `Dy6`. One combatant in a real session took seven
// killing blows and reached `Dy21` - being shot while bleeding out was
// the best thing that could happen to him.
//
// Scoped to the death floor's own writes. Critical-table `Dy` keeps the
// stacking it has always had for everybody else: changing that is a
// rules change for every table, and this project does not make those
// quietly.
// ============================================================
import { condType, condDuration, buildCondCode } from '../conditions';
import { resolveDeathFloor, type Protagonist } from './protagonist';

export interface MortalBlow {
  /** Condition codes to write in place of the death. */
  conditions: string[];
  /**
   * The clock ran out under this blow and nothing caught him.
   *
   * Only ever true for `dyingFatal`, the one floor that lets go - and it
   * is the same answer that floor gives when the clock expires on its
   * own, reached a different way.
   */
  fatal: boolean;
  note: string;
}

/** The longest running `Dy` clock in a condition list, or 0 if there is none. */
export function currentClock(conditions: readonly string[]): number {
  let longest = 0;
  for (const c of conditions) {
    if (condType(c) !== 'Dy') continue;
    const d = condDuration(c);
    if (d > longest) longest = d;
  }
  return longest;
}

/**
 * What a killing blow does to a protagonist.
 *
 * Returns null when death stands - no floor, or a floor of `'none'`.
 *
 * The FIRST mortal wound starts the clock. Every one after it takes a
 * round off, and when the last round goes the blow resolves exactly as
 * the clock expiring would: unconscious for the floors that guarantee
 * survival, death for the one that does not.
 */
export function resolveMortalBlow(
  prot: Protagonist | undefined,
  currentConditions: readonly string[] = [],
): MortalBlow | null {
  // A CORPSE IS NOT SAVED AGAIN. The floor stands between a character and
  // dying; it has nothing to say to one who already died. Without this,
  // shooting a dead protagonist found no clock to shorten, started a
  // fresh one, and left them carrying both `K` and `Dy3` at once -
  // reported from play.
  //
  // Unconscious is deliberately NOT in this check: someone the floor put
  // under is alive, and a further mortal wound should start him bleeding
  // out, which is a worse place to be.
  if (currentConditions.some((c) => condType(c) === 'K')) return null;

  const first = resolveDeathFloor(prot, 'kill');
  if (!first) return null;

  const clock = currentClock(currentConditions);
  // Not already on a clock - the ordinary case, and the ordinary note.
  if (clock <= 0) return { conditions: first.conditions, fatal: false, note: first.note };

  return blowOnTheClock(prot, clock - 1);
}

/** A mortal blow to someone already dying: a round off the clock, or the clock run out. */
function blowOnTheClock(prot: Protagonist | undefined, shortened: number): MortalBlow {
  if (shortened > 0) {
    return {
      conditions: [buildCondCode('Dy', shortened)],
      fatal: false,
      note: `✴ Another mortal wound - ${shortened} round${shortened === 1 ? '' : 's'} left to reach him.`,
    };
  }
  // The clock ran out under the blow. Ask the floor the same question the
  // countdown asks, so the two ways of running out cannot disagree.
  const expiry = resolveDeathFloor(prot, 'dyingExpired');
  if (!expiry) {
    return { conditions: [], fatal: true, note: '✴ The last round went with the blow. Nobody reached him.' };
  }
  return { conditions: expiry.conditions, fatal: false, note: expiry.note };
}
